<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMobileSwipeClose } from '../../../composables/useMobileSwipeClose';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import Button from 'dashboard/components-next/button/Button.vue';
import WooCommerceBrandIcon from '../../WooCommerceBrandIcon.vue';
import WooCommerceOrdersList from './WooCommerceOrdersList.vue';
import {
  hasWooCommerceConversationModeHooks,
  WOO_COMMERCE_CONVERSATION_PANEL_MODES,
} from '../../../helpers/stores';

const CONVERSATION_ROUTES = [
  'inbox_conversation',
  'conversation_through_inbox',
  'conversations_through_label',
  'team_conversations_through_label',
  'conversations_through_folders',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversation_through_participating',
  'inbox_view_conversation',
];

const route = useRoute();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');
const integration = useFunctionGetter(
  'integrations/getIntegration',
  'woocommerce'
);
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const isConversationRoute = computed(() =>
  CONVERSATION_ROUTES.includes(route.name)
);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() =>
  contactId.value ? contactGetter.value(contactId.value) : null
);
const hasSidebarModeHooks = computed(() =>
  hasWooCommerceConversationModeHooks(
    integration.value?.hooks || [],
    WOO_COMMERCE_CONVERSATION_PANEL_MODES.SIDEBAR
  )
);
const shouldShowPanel = computed(() => {
  return (
    uiSettings.value.is_woocommerce_sidebar_open &&
    isConversationRoute.value &&
    Boolean(currentChat.value?.id) &&
    hasSidebarModeHooks.value
  );
});

const dismissPanel = () => {
  updateUISettings({
    is_woocommerce_sidebar_open: false,
  });
};

const closePanelOnMobile = () => {
  if (isSmallScreen.value && shouldShowPanel.value) {
    dismissPanel();
  }
};

const { onTouchStart: onSidebarTouchStart, onTouchEnd: onSidebarTouchEnd } =
  useMobileSwipeClose({
    canTrack: () => isSmallScreen.value && shouldShowPanel.value,
    onClose: dismissPanel,
    direction: 'right',
  });

watch(contactId, nextContactId => {
  if (nextContactId) {
    store.dispatch('contacts/show', { id: nextContactId });
  }
});

watch(
  [hasSidebarModeHooks, isConversationRoute],
  ([hasHooks, isConversation]) => {
    if (!hasHooks || !isConversation) {
      dismissPanel();
    }
  }
);

watch(
  () => [
    uiSettings.value?.is_contact_sidebar_open,
    uiSettings.value?.is_copilot_panel_open,
  ],
  ([isContactSidebarOpen, isCopilotPanelOpen], previousValues) => {
    if (!uiSettings.value?.is_woocommerce_sidebar_open) {
      return;
    }

    if (!previousValues) {
      return;
    }

    const [previousContactSidebarOpen, previousCopilotPanelOpen] =
      previousValues;

    const contactSidebarOpened =
      isContactSidebarOpen && !previousContactSidebarOpen;
    const copilotPanelOpened = isCopilotPanelOpen && !previousCopilotPanelOpen;

    if (contactSidebarOpened || copilotPanelOpened) {
      dismissPanel();
    }
  }
);

onMounted(() => {
  store.dispatch('integrations/get', 'woocommerce').catch(() => {});
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }

  if (
    uiSettings.value?.is_woocommerce_sidebar_open &&
    (uiSettings.value?.is_contact_sidebar_open ||
      uiSettings.value?.is_copilot_panel_open)
  ) {
    dismissPanel();
  }
});
</script>

<template>
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="() => closePanelOnMobile()"
    data-target="conversation-woocommerce-sidebar"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] md:self-stretch md:shrink-0 ltr:md:ml-auto rtl:md:mr-auto ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowPanel,
        'md:hidden': !shouldShowPanel,
      },
    ]"
    @touchstart.passive="onSidebarTouchStart"
    @touchend.passive="onSidebarTouchEnd"
  >
    <div
      class="flex h-12 items-center justify-between border-b border-n-weak px-4"
    >
      <div class="flex min-w-0 items-center gap-2">
        <WooCommerceBrandIcon :size="18" :rounded="false" />
        <div class="min-w-0">
          <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.PANEL_TITLE') }}
          </p>
          <p
            v-if="contact?.name || contact?.email"
            class="mb-0 truncate text-xs text-n-slate-10"
          >
            {{ contact?.name || contact?.email }}
          </p>
        </div>
      </div>

      <Button
        ghost
        slate
        sm
        class="!rounded-full"
        icon="i-lucide-x"
        @click="dismissPanel"
      />
    </div>

    <div class="flex-1 overflow-y-auto">
      <WooCommerceOrdersList
        v-if="contactId"
        :contact-id="contactId"
        conversation-mode="sidebar"
      />
      <div v-else class="px-4 py-6 text-sm text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.NO_ORDERS') }}
      </div>
    </div>
  </div>
  <template v-else />
</template>
